// src/pages/PublicOfferPortal.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAngreFokus, useSideTittel } from "../components/tilgjengelighet";
import { TilbudTopp, TilbudOversikt, fmtDate } from "./tilbud/TilbudVisning";
import { butikkFor } from "../tilbud/butikker";
import { SPORT1_LOGO } from "../kundesider";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const UNDO_GRACE_MS = 3000;

/* ---- Format-hjelpere ---- */
const fmtDT = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(+dt)
    ? "—"
    : dt.toLocaleString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", "");
};


/* ---- Meldingsboble ---- */
function Bubble({ msg }) {
  const isEmployee = msg.from === "employee";
  return (
    <div className={`flex ${isEmployee ? "justify-start" : "justify-end"} mb-2`}>
      <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${isEmployee ? "bg-gray-100 text-gray-900" : "bg-blue-600 text-white"}`}>
        <div className={`font-medium text-xs mb-1 ${isEmployee ? "text-gray-700" : "text-white"}`}>
          {isEmployee
            ? (msg.fromName || "Butikken").trim().split(/\s+/)[0]
            : (msg.fromName || "Deg")}
        </div>
        <div style={{ whiteSpace: "pre-wrap" }}>{msg.text}</div>
        <div className={`text-xs mt-1 text-right ${isEmployee ? "text-gray-600" : "text-white"}`}>{fmtDT(msg.createdAt)}</div>
      </div>
    </div>
  );
}

/* ==============================================================
   Hovedkomponent
   ============================================================== */
export default function PublicOfferPortal() {
  const { token } = useParams();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  /* Godkjenning */
  const [approveNote, setApproveNote] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [pendingDecision, setPendingDecision] = useState(null); // { type: "approve"|"decline", note }
  const pendingTimerRef = useRef(null);

  /* Meldinger */
  const [messageText, setMessageText] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [showAllMessages, setShowAllMessages] = useState(false);
  const chatEndRef = useRef(null);

  /* E-post */

  useSideTittel(offer?.offerRef ? `Tilbud #${offer.offerRef} – Sport 1` : "Tilbud – Sport 1");
  const { angreRef, startRef, vedtakRef } = useAngreFokus(
    !!pendingDecision,
    typeof offer?.quote?.approved?.ok === "boolean" && !!offer?.quote?.approved?.at
  );

  const PUBLIC_BASE = useMemo(() => `${API_BASE_URL}/offers/public/${token}`, [token]);

  /* --- Spor åpning (én gang) --- */
  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (!token || hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    // Åpnet fra /offer-details («Åpne», ?staffPreview=1): telles som butikkens forhåndsvisning.
    const fraButikk = new URLSearchParams(window.location.search).get("staffPreview") === "1";
    fetch(`${PUBLIC_BASE}/open`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: fraButikk ? "staff" : "customer" }),
    }).catch(() => {});
  }, [token, PUBLIC_BASE]);

  /* --- Hent tilbud --- */
  async function fetchOffer(signal) {
    const r = await fetch(`${PUBLIC_BASE}?t=${Date.now()}`, { signal, cache: "no-store" });
    if (!r.ok) { const b = await r.json().catch(() => ({})); const e = new Error(r.status === 410 && b.message ? b.message : "Fant ikke tilbudet"); e.expired = r.status === 410; throw e; }
    return r.json();
  }

  useEffect(() => {
    if (!token) return;
    const ctrl = new AbortController();
    setLoading(true);
    setErr("");
    fetchOffer(ctrl.signal)
      .then(setOffer)
      .catch((e) => { if (e.name !== "AbortError") setErr(e.expired ? e.message : "Lenken er ugyldig eller utløpt."); })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* --- Rydd timer ved unmount --- */
  useEffect(() => () => { if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current); }, []);

  /* --- Beregn totaler lokalt (sikkerhet mot at backend mangler) --- */
  const totalPris = useMemo(() => {
    if (!offer) return 0;
    if (offer.totalPris) return offer.totalPris;
    return (offer.produkter || []).reduce((s, p) => s + (Number(p.totalprisEtterRabatt) || 0), 0);
  }, [offer]);

  const messages = useMemo(() => {
    const raw = Array.isArray(offer?.messages) ? offer.messages : [];
    return [...raw].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [offer?.messages]);

  const messagesToShow = useMemo(
    () => (showAllMessages || messages.length <= 3 ? messages : messages.slice(-3)),
    [messages, showAllMessages]
  );

  /* --- Godkjenning med angrefrist --- */
  async function doApprove(note, decline = false) {
    setActionBusy(true);
    try {
      const r = await fetch(`${PUBLIC_BASE}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note, decline }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.message || "Feil");
      const fresh = await fetchOffer();
      setOffer(fresh);
      setShowAllMessages(true);
      setActionMsg(decline ? "Tilbudet er avslått. Takk for tilbakemeldingen." : "Tilbudet er godkjent. Takk!");
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch (e) {
      // Serverens melding når den er til kunden (f.eks. at tilbudet har utløpt).
      setActionMsg(e.message && e.message !== "Feil" ? e.message : "Beklager, noe gikk galt. Kontakt butikken.");
    } finally {
      setActionBusy(false);
    }
  }

  function startDecision(type) {
    if (pendingTimerRef.current) { clearTimeout(pendingTimerRef.current); pendingTimerRef.current = null; }
    const note = approveNote.trim();
    setPendingDecision({ type, note });
    setActionMsg(
      type === "approve"
        ? "Godkjenning registrert — lagres om noen sekunder. Du kan angre."
        : "Avslag registrert — lagres om noen sekunder. Du kan angre."
    );
    pendingTimerRef.current = setTimeout(() => {
      doApprove(note, type === "decline");
      setPendingDecision(null);
      pendingTimerRef.current = null;
    }, UNDO_GRACE_MS);
  }

  function cancelDecision() {
    if (pendingTimerRef.current) { clearTimeout(pendingTimerRef.current); pendingTimerRef.current = null; }
    setPendingDecision(null);
    setActionMsg("Valget er angret.");
  }

  /* --- Send melding --- */
  async function sendMessage() {
    const text = messageText.trim();
    if (!text) return;
    setSendingMsg(true);
    setMsgError("");
    try {
      const r = await fetch(`${PUBLIC_BASE}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, fromName: offer?.customerSnapshot?.firstName || "Kunde" }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.message || "Feil");
      if (Array.isArray(body.messages)) {
        setOffer((prev) => prev ? { ...prev, messages: body.messages } : prev);
      }
      setMessageText("");
      setShowAllMessages(true);
    } catch (e) {
      setMsgError(e.message || "Kunne ikke sende melding.");
    } finally {
      setSendingMsg(false);
    }
  }


  /* ---- Render ---- */
  if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-600" role="status">Laster tilbud…</div>;
  if (err || !offer)
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-2">Tilbud</h1>
        <p className={err ? "text-red-700" : "text-gray-600"} role={err ? "alert" : undefined}>{err || "Tilbud ikke funnet."}</p>
      </div>
    );

  const snap = offer.customerSnapshot || {};
  const approval = offer.quote?.approved || null;
  const hasDecision = typeof approval?.ok === "boolean" && approval?.at;
  const isApproved = approval?.ok === true;
  const isDeclined = hasDecision && !isApproved;
  const quoteEnabled = offer.quote?.enabled !== false;
  // erstattet: butikken har sendt en ny versjon med ny lenke (src/lib/tilbudStatus.js).
  const isReplaced = offer.status === "erstattet";
  const isClosed = offer.status === "godkjent" || offer.status === "avslatt" || offer.status === "kansellert" || isReplaced;
  const isExpired = offer.utloper && new Date(offer.utloper) < new Date();
  const chatLocked = isExpired || isReplaced;

  const customerName = `${snap.firstName || ""} ${snap.lastName || ""}`.trim() || "Kunde";
  const butikk = butikkFor(offer.butikkid);

  return (
    <div className="mx-auto w-full max-w-2xl px-3 sm:px-4 py-5 sm:py-10 space-y-4 sm:space-y-6">

      {/* Pristilbud kommer alltid fra en butikk: Sport 1-logoen, som i nettbutikken. */}
      <img src={SPORT1_LOGO} alt={butikk.navn} className="h-10 w-auto object-contain" />

      {/* ---- Topp (felles med forhåndsvisningen i ansatt-appen) ---- */}
      <TilbudTopp
        offer={offer}
        merke={
          hasDecision ? (
            <span className={`shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${isApproved ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
              {isApproved ? <><span aria-hidden="true">✓ </span>Godkjent</> : "Avslått"}
            </span>
          ) : isClosed ? (
            <span className="shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-gray-100 text-gray-600">{isReplaced ? "Erstattet" : offer.status}</span>
          ) : (
            <span className="shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-blue-50 text-blue-700">Til godkjenning</span>
          )
        }
      />

      {/* ---- Kundehilsen ---- */}
      <p className="text-gray-700 text-sm">Hei{snap.firstName ? ` ${snap.firstName}` : ""},</p>

      {/* ---- Statusbannere ---- */}
      {hasDecision && isApproved && (
        <div ref={vedtakRef} tabIndex={-1} className="rounded-xl border-2 border-green-400 bg-green-50 p-4">
          <div className="font-bold text-green-900"><span aria-hidden="true">✅ </span>Du har godkjent tilbudet</div>
          <div className="text-sm text-green-800 mt-1">
            Godkjent {fmtDT(approval.at)}{approval.note ? ` — "${approval.note}"` : ""}
          </div>
          <div className="text-sm text-green-700 mt-1">Vi tar kontakt når varene er klare.</div>
        </div>
      )}
      {hasDecision && isDeclined && (
        <div ref={vedtakRef} tabIndex={-1} className="rounded-xl border-2 border-red-400 bg-red-50 p-4">
          <div className="font-bold text-red-900">Tilbudet er avslått</div>
          <div className="text-sm text-red-800 mt-1">
            Avslått {fmtDT(approval.at)}{approval.note ? ` — "${approval.note}"` : ""}
          </div>
          <div className="text-sm text-red-700 mt-1">Ta gjerne kontakt om du har spørsmål.</div>
        </div>
      )}
      {isReplaced && (
        <div role="status" className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4 text-amber-900">
          <div className="font-bold">Tilbudet er erstattet</div>
          <div className="text-sm mt-1">Butikken har sendt deg en annen lenke til tilbudet som gjelder, på SMS eller e-post. Bruk den nyeste lenken for å se og godkjenne tilbudet.</div>
        </div>
      )}
      {isExpired && !hasDecision && !isReplaced && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          <span aria-hidden="true">⚠ </span>Dette tilbudet utløp {fmtDate(offer.utloper)}. Kontakt oss for et oppdatert tilbud.
        </div>
      )}

      {/* ---- Tilbudsinfo (felles med forhåndsvisningen) ---- */}
      <TilbudOversikt offer={offer} totalPris={totalPris} />

      {/* ---- Godkjenningspanel ---- */}
      {quoteEnabled && !hasDecision && !isClosed && (
        <section className="rounded-xl border-2 border-blue-300 bg-blue-50 shadow-sm overflow-hidden">
          <div className="bg-blue-600 px-4 py-3">
            <h2 className="font-semibold text-white">Godkjenn tilbudet</h2>
          </div>
          <div className="p-4 space-y-4">
            <p className="text-sm text-blue-900">
              Gjennomgå tilbudet og trykk godkjenn om du ønsker å gå videre.
            </p>

            <label htmlFor="tilbud-kommentar" className="block text-sm font-medium text-blue-900 -mb-2">
              Kommentar til butikken (valgfritt)
            </label>
            <textarea
              id="tilbud-kommentar"
              rows={2}
              className="w-full rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="F.eks. når du kan hente"
              value={approveNote}
              onChange={(e) => setApproveNote(e.target.value)}
            />

            {pendingDecision ? (
              <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 gap-3">
                <span className="text-amber-900 font-medium text-sm" aria-hidden="true">{actionMsg}</span>
                <button
                  ref={angreRef}
                  type="button"
                  onClick={cancelDecision}
                  className="shrink-0 px-4 py-2 rounded-lg bg-amber-200 hover:bg-amber-300 text-amber-900 font-semibold text-sm"
                >
                  Angre
                </button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  ref={startRef}
                  type="button"
                  onClick={() => startDecision("approve")}
                  disabled={actionBusy}
                  className="w-full sm:flex-1 py-4 sm:py-3 rounded-xl bg-green-700 hover:bg-green-800 active:bg-green-900 text-white font-semibold text-base sm:text-sm disabled:opacity-50 shadow"
                >
                  {actionBusy ? "Sender…" : <><span aria-hidden="true">✓ </span>Godkjenn tilbud</>}
                </button>
                <button
                  type="button"
                  onClick={() => startDecision("decline")}
                  disabled={actionBusy}
                  className="w-full sm:w-auto sm:flex-none py-4 sm:py-3 px-6 rounded-xl bg-white hover:bg-gray-50 active:bg-gray-100 border border-gray-300 text-gray-700 font-semibold text-base sm:text-sm disabled:opacity-50"
                >
                  Avslå
                </button>
              </div>
            )}

            {/* Leses opp både under angrefristen og etterpå (U6) */}
            <p className={pendingDecision ? "sr-only" : "text-sm text-blue-900 font-medium"} role="status">
              {actionMsg}
            </p>
          </div>
        </section>
      )}

      {/* ---- Meldinger / chat ---- */}
      <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="bg-gray-50 border-b px-4 py-3">
          <h2 className="font-semibold text-gray-900 text-sm">Meldinger</h2>
          <p className="text-xs text-gray-500 mt-0.5">Chat direkte med butikken her</p>
        </div>

        <div className="p-3 sm:p-4">
          {messages.length === 0 ? (
            <p className="text-center text-sm text-gray-600 py-4">Ingen meldinger ennå.</p>
          ) : (
            <>
              {messages.length > 3 && (
                <button
                  type="button"
                  onClick={() => setShowAllMessages((v) => !v)}
                  aria-expanded={showAllMessages}
                  className="w-full py-2 text-xs text-gray-600 hover:text-gray-900 text-center mb-3 underline"
                >
                  {showAllMessages ? "Vis bare de siste meldingene" : `Vis alle ${messages.length} meldinger`}
                </button>
              )}
              <div className="space-y-1">
                {messagesToShow.map((m) => (
                  <Bubble key={m._id || m.createdAt} msg={m} />
                ))}
              </div>
              <div ref={chatEndRef} />
            </>
          )}

          {!chatLocked ? (
            <div className="mt-3 space-y-2">
              <label htmlFor="tilbud-melding" className="block text-xs font-medium text-gray-700">
                Din melding til butikken
              </label>
              <textarea
                id="tilbud-melding"
                rows={3}
                className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Skriv en melding til butikken…"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              />
              <button
                onClick={sendMessage}
                disabled={sendingMsg || !messageText.trim()}
                className="w-full sm:w-auto sm:float-right px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold disabled:opacity-50"
              >
                {sendingMsg ? "Sender…" : "Send melding"}
              </button>
            </div>
          ) : (
            <p className="mt-4 text-center text-xs text-gray-600">{isReplaced ? "Dialogen er stengt. Skriv til butikken på den nye lenken." : "Dialogen er stengt."}</p>
          )}
          {msgError && <p className="mt-2 text-xs text-red-700" role="alert">{msgError}</p>}
        </div>
      </section>

      {/* ---- Kontaktinfo ---- */}
      <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="bg-gray-50 border-b px-4 py-3">
          <h2 className="font-semibold text-gray-900 text-sm">Din kontaktinfo</h2>
        </div>
        {/* Kontaktinfo endres på Din side, ikke via lenken (kundeflyt fase 3) */}
        <div className="p-4 text-sm text-gray-700">
          <div className="font-medium break-words">{customerName}</div>
          <p className="mt-1">
            Vil du endre e-post eller mobilnummer? Det gjør du på{" "}
            {/* Relativ, som de andre portalsidene: Din side på samme domene som lenken. */}
            <a href="/min-side" className="text-blue-700 underline font-medium">
              Din side
            </a>
            .
          </p>
        </div>
      </section>

      {/* ---- Footer ---- */}
      {/* Butikken som gir tilbudet (Offer.butikkid). */}
      <p className="text-center text-xs text-gray-600 pb-6 [overflow-wrap:anywhere]">
        {butikk.selskap} · {butikk.adresse} · Tlf. {butikk.telefon} ·{" "}
        <a href={`mailto:${butikk.epost}`} className="underline">{butikk.epost}</a>
      </p>

    </div>
  );
}
